import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  BusinessCenterOutlined as BusinessCenterOutlinedIcon,
  EventOutlined as EventOutlinedIcon,
  ChecklistOutlined as ChecklistOutlinedIcon,
  FormatListBulletedOutlined as FormatListBulletedOutlinedIcon,
  AssignmentIndOutlined as AssignmentIndOutlinedIcon,
  InsightsOutlined as InsightsOutlinedIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import OpportunityFormModal from './OpportunityFormModal';
import OpportunityFilters from './components/forms/OpportunityFilters';
import OpportunitiesTable from './components/tables/OpportunitiesTable';
import OpportunityStatsBar from './components/ui/OpportunityStatsBar';
import OwnerOpportunitiesTab from './components/tabs/OwnerOpportunitiesTab';
import WorkOverviewTab from './components/tabs/WorkOverviewTab';
import FinishJobDialog from './components/dialogs/FinishJobDialog';
import { OpportunityCalendarModal } from './components/dialogs/OpportunityCalendarModal';
import { OpportunityTaskListModal } from './components/dialogs/OpportunityTaskListModal';
import { OpportunityPopup } from './components/OpportunityPopup';
import { useOpportunities } from './hooks/useOpportunities';
import { useFinishJob } from './hooks/useFinishJob';
import * as actions from '../../redux/actions';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { filterMarketingEmployees } from './utils/employee';

// FinishJobDialog เดิมรับ showSnack(message, severity) จากหน้า detail (useOpportunityDetail มี
// Snackbar ของตัวเอง) — หน้า list นี้ไม่มี ใช้ showAlert (dialog กลางของทั้งแอป) แทน ให้พฤติกรรม
// เดียวกัน (error = badge แดง, สำเร็จ = badge เขียว)
const showSnackViaAlert = (message, severity) => showAlert(message, severity === 'error' ? { status: 400 } : undefined);

// สไตล์ pill segmented control เดียวกับ TYPE_TABS ใน HrReportV2.js (พนักงานรายวัน/รายเดือน) —
// ใช้ gradient ต่อแท็บแทนขีดเส้นใต้แบบ MUI Tabs เดิม
const MAIN_TABS = [
  { value: 0, label: 'รายการทั้งหมด', icon: FormatListBulletedOutlinedIcon, gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)' },
  { value: 1, label: 'งานของผู้รับผิดชอบ', icon: AssignmentIndOutlinedIcon, gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' },
  { value: 2, label: 'ภาพรวมการทำงาน', icon: InsightsOutlinedIcon, gradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)' },
];

export default function OpportunityList() {
  const {
    rows,
    loading,
    loadingMore,
    hasMore,
    q,
    statusFilter,
    typeFilter,
    ownerFilter,
    contactFilter,
    dueDateFrom,
    dueDateTo,
    sortBy,
    sortOrder,
    changeQuery,
    toggleStatusFilter,
    setStatusFilter,
    setTypeFilter,
    setOwnerFilter,
    setContactFilter,
    setDueDateFrom,
    setDueDateTo,
    setSortBy,
    setSortOrder,
    changeStatus,
    scrollRef,
    handleScroll,
    refetch,
  } = useOpportunities();

  // เลือกสถานะ "ได้งาน" จาก dropdown ในตาราง → เปิด modal จบงาน (ล็อกผลเป็น WON ไม่มี toggle
  // ให้เลือกไม่ได้งาน เพราะ dropdown บอกเจตนาชัดแล้ว) ก่อนค่อยเปลี่ยนสถานะจริงตอนกดยืนยัน — สถานะ
  // อื่นๆ (รวม LOST/CANCELLED) ยังเปลี่ยนตรงๆ ทันทีเหมือนเดิม ไม่มี modal เพิ่ม
  const finishJob = useFinishJob({ onChanged: refetch, showSnack: showSnackViaAlert });

  // เอาไว้ resolve owner_id (Mongo Employee) → ชื่อ ที่คอลัมน์ Owner ในตาราง — ดึงทั้งบริษัทครั้งเดียว
  // แทนการ lookup ทีละแถว (owner_id ไม่มี name snapshot เก็บไว้เหมือน contact) ใช้รายชื่อชุด
  // เดียวกันนี้เป็น option ของตัวกรอง "Owner" ด้วย
  const dispatch = useDispatch();
  const employee = useSelector((state) => state.employee);
  useEffect(() => {
    dispatch(actions.employeeAll({ size: 9999, activeOnly: false }));
  }, [dispatch]);
  const ownerNameById = useMemo(() => {
    const map = {};
    (employee?.rows || []).forEach((e) => {
      map[e._id || e.id] = formatEmployeeThaiName(e);
    });
    return map;
  }, [employee?.rows]);
  // resolve created_by (User._id, auto จาก req.user.id ตอนสร้าง) → ชื่อ ที่คอลัมน์ "ผู้สร้าง" —
  // คนละ keyspace จาก owner_id/ordered_by (Employee._id ตรงๆ) ต้อง match ผ่าน employee.uid แทน
  const creatorNameByUserId = useMemo(() => {
    const map = {};
    (employee?.rows || []).forEach((e) => {
      if (e.uid) map[e.uid] = formatEmployeeThaiName(e);
    });
    return map;
  }, [employee?.rows]);
  // ก้อนพนักงานเต็ม (รูปโปรไฟล์/แผนก) ให้แท็บ "งานของผู้รับผิดชอบ" ทำการ์ด — ใช้ employee.rows
  // ชุดเดียวกับด้านบน ไม่ fetch ซ้ำ
  const employeeById = useMemo(() => {
    const map = {};
    (employee?.rows || []).forEach((e) => {
      map[e._id || e.id] = e;
    });
    return map;
  }, [employee?.rows]);
  const ownerOptions = useMemo(
    () =>
      filterMarketingEmployees(employee?.rows).map((e) => ({
        id: e._id || e.id,
        name: formatEmployeeThaiName(e),
      })),
    [employee?.rows],
  );

  // คลิกหัวตารางเพื่อ sort — วนครบ 3 คลิกต่อคอลัมน์: asc → desc → กลับ default (เรียงตาม
  // created_at/desc เหมือนตอนเปิดหน้าแรก, ไม่มีคอลัมน์ไหน active) คลิกคอลัมน์ใหม่ = เริ่มที่ asc
  // เสมอไม่ว่าคอลัมน์เดิมจะอยู่ทิศไหน (ค่า default อ้างอิงจาก initial state ใน useOpportunities.js
  // ต้องแก้คู่กันถ้าเปลี่ยนค่าเริ่มต้นตรงนั้น)
  const DEFAULT_SORT_BY = 'created_at';
  const DEFAULT_SORT_ORDER = 'desc';
  const handleSortChange = (field) => {
    if (field !== sortBy) {
      setSortBy(field);
      setSortOrder('asc');
    } else if (sortOrder === 'asc') {
      setSortOrder('desc');
    } else {
      setSortBy(DEFAULT_SORT_BY);
      setSortOrder(DEFAULT_SORT_ORDER);
    }
  };

  const clearFilters = () => {
    changeQuery('');
    setStatusFilter([]);
    setTypeFilter('');
    setOwnerFilter('');
    setContactFilter(null);
    setDueDateFrom('');
    setDueDateTo('');
  };

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [taskListOpen, setTaskListOpen] = useState(false);
  const [mainTab, setMainTab] = useState(0); // 0 = รายการทั้งหมด, 1 = งานของผู้รับผิดชอบ

  const [dialog, setDialog] = useState({ open: false, opportunity: null });
  const openCreate = () => setDialog({ open: true, opportunity: null });
  // แก้ไขผ่านฟอร์มเต็ม (โหมด edit ของ OpportunityFormModal มีอยู่แล้วแต่ไม่เคยมีปุ่มไหนเรียกใช้เลย
  // ก่อนหน้านี้ — row จากตาราง list มี field ครบตามที่ useOpportunityForm.js ต้องใช้ตอน isEdit อยู่แล้ว
  // (LIST_SELECT_FIELDS ฝั่ง backend คลุมหมด) ไม่ต้อง fetch เพิ่ม
  const openEdit = (row) => setDialog({ open: true, opportunity: row });
  const closeDialog = () => setDialog({ open: false, opportunity: null });

  // ลบจริง (hard delete) — ยืนยันด้วย confirmAsync ตาม convention เดิมของแอป (ดู
  // window.confirm ใน ProjectDetail.js/ExpensesDetail.js ฯลฯ) ไม่มีทาง undo หลังกดยืนยัน
  const handleDelete = async (row) => {
    if (!(await confirmAsync(`ยืนยันลบโอกาสทางธุรกิจ "${row.project_name}" ใช่หรือไม่? ข้อมูลจะถูกลบถาวร ไม่สามารถกู้คืนได้`))) return;
    try {
      await dispatch(actions.deleteOpportunity({ id: row.id }));
      refetch();
    } catch (err) {
      showAlert(err.message || 'ลบไม่สำเร็จ', { status: err?.response?.status });
    }
  };

  // popup รายละเอียด — แทนการ navigate ไปหน้าเต็ม /marketing/opportunities/:id เดิม
  const [openId, setOpenId] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const openPopup = (id) => {
    setOpenId(id);
    setMinimized(false);
  };
  const closePopup = () => {
    setOpenId(null);
    setMinimized(false);
    refetch();
  };
  const toggleMinimize = () => setMinimized((m) => !m);
  const popOut = () => {
    if (!openId) return;
    window.open(
      `/marketing-popup/${openId}`,
      `marketing-popup-${openId}`,
      'width=1200,height=820,left=100,top=60',
    );
    setOpenId(null);
    setMinimized(false);
  };

  // popped-out window ส่ง 'pop-in' กลับมาตอนกดปิด/ย้ายกลับ — เปิด popup ต่อในแท็บนี้
  useEffect(() => {
    const handler = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'pop-in' && e.data.id) {
        setOpenId(e.data.id);
        setMinimized(false);
        refetch();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [refetch]);

  const theme = useTheme();

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #3730a3 0%, #6366f1 100%)',
              color: '#fff',
            }}
          >
            <BusinessCenterOutlinedIcon />
          </Box>
          <Box>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                backgroundImage: 'linear-gradient(135deg, #3730a3, #6366f1)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              โอกาสทางธุรกิจ
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ติดตามดีลตั้งแต่ติดต่อลูกค้าจนถึงปิดงาน
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<ChecklistOutlinedIcon />}
            onClick={() => setTaskListOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            รายการงานทั้งหมด
          </Button>
          <Button
            variant="outlined"
            startIcon={<EventOutlinedIcon />}
            onClick={() => setCalendarOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            ดูปฏิทิน
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              borderRadius: 2,
              boxShadow: `0 8px 20px -6px ${alpha(
                theme.palette.primary.main,
                0.5,
              )}`,
            }}
          >
            เพิ่มโอกาสทางธุรกิจ
          </Button>
        </Stack>
      </Stack>

      <OpportunityStatsBar rows={rows} />

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            gap: 0.5,
            p: 0.5,
            borderRadius: '14px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: (theme) => (theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0,0,0,0.35)'
              : '0 4px 20px rgba(15,23,42,0.06)'),
          }}
        >
          {MAIN_TABS.map((t) => {
            const selected = mainTab === t.value;
            const Icon = t.icon;
            return (
              <Box
                key={t.value}
                onClick={() => setMainTab(t.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.75,
                  py: 0.75,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease',
                  transform: selected ? 'translateY(-1px)' : 'none',
                  background: selected ? t.gradient : 'transparent',
                  boxShadow: selected ? '0 6px 16px -4px rgba(0,0,0,0.35)' : 'none',
                  '&:hover': {
                    background: selected ? t.gradient : (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)'),
                  },
                }}
              >
                <Icon sx={{ fontSize: 17, color: selected ? '#fff' : 'text.secondary' }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: selected ? 700 : 500, color: selected ? '#fff' : 'text.secondary' }}>
                  {t.label}
                </Typography>
              </Box>
            );
          })}
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {mainTab === 0 ? (
          <>
            <OpportunityFilters
              q={q}
              onQChange={changeQuery}
              statusFilter={statusFilter}
              onStatusFilterToggle={toggleStatusFilter}
              typeFilter={typeFilter}
              onTypeFilterChange={setTypeFilter}
              ownerFilter={ownerFilter}
              onOwnerFilterChange={setOwnerFilter}
              ownerOptions={ownerOptions}
              contactFilter={contactFilter}
              onContactFilterChange={setContactFilter}
              dueDateFrom={dueDateFrom}
              onDueDateFromChange={setDueDateFrom}
              dueDateTo={dueDateTo}
              onDueDateToChange={setDueDateTo}
              onClearFilters={clearFilters}
            />
            <OpportunitiesTable
              rows={rows}
              loading={loading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              q={q}
              scrollRef={scrollRef}
              onScroll={handleScroll}
              ownerNameById={ownerNameById}
              creatorNameByUserId={creatorNameByUserId}
              onRowClick={(row) => openPopup(row.id)}
              // คลิกซ้ำที่ลูกค้าเดิมที่กรองอยู่แล้ว = ยกเลิกตัวกรอง (toggle) — เทียบกับ
              // contactFilter ปัจจุบันตรงๆ ไม่ใช่แค่เซ็ตทับทุกครั้ง
              onFilterByContact={(row) => {
                if (!row.contact_id) return;
                const currentId = contactFilter?._id || contactFilter?.id;
                if (currentId === row.contact_id) {
                  setContactFilter(null);
                } else {
                  setContactFilter({ _id: row.contact_id, name: row.contact_name_snapshot || '' });
                }
              }}
              // ownerFilter เป็น string เดี่ยว (owner_id) ไม่ใช่ object เหมือน contactFilter —
              // toggle ตรงๆ ได้เลย
              onFilterByOwner={(row) => {
                if (!row.owner_id) return;
                setOwnerFilter(ownerFilter === row.owner_id ? '' : row.owner_id);
              }}
              // ให้ตารางใส่สี highlight ต่างจากปกติตอนกำลังกรองด้วยค่านั้นอยู่จริง
              activeContactId={contactFilter?._id || contactFilter?.id || null}
              activeOwnerId={ownerFilter || null}
              onStatusChange={(row, toStatus) => (
                toStatus === 'WON' ? finishJob.openDialog(row) : changeStatus(row.id, toStatus)
              )}
              onEditRow={openEdit}
              onDeleteRow={handleDelete}
              onSaved={refetch}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          </>
        ) : mainTab === 1 ? (
          <OwnerOpportunitiesTab
            employeeById={employeeById}
            onOpportunityClick={openPopup}
          />
        ) : (
          <WorkOverviewTab
            employeeById={employeeById}
            ownerNameById={ownerNameById}
          />
        )}
      </Paper>

      <FinishJobDialog
        open={finishJob.open}
        result={finishJob.result}
        onResultChange={finishJob.setResult}
        poNumber={finishJob.poNumber}
        onPoNumberChange={finishJob.setPoNumber}
        value={finishJob.value}
        onValueChange={finishJob.setValue}
        note={finishJob.note}
        onNoteChange={finishJob.setNote}
        docs={finishJob.docs}
        onAddDoc={finishJob.addDocRow}
        onRemoveDoc={finishJob.removeDocRow}
        onDocNameChange={finishJob.setDocName}
        onDocFileChange={finishJob.setDocFile}
        saving={finishJob.saving}
        onClose={finishJob.closeDialog}
        onConfirm={finishJob.confirm}
        hideResultToggle
      />

      <OpportunityFormModal
        open={dialog.open}
        opportunity={dialog.opportunity}
        onClose={closeDialog}
        onSaved={refetch}
      />

      <OpportunityPopup
        id={openId}
        minimized={minimized}
        onClose={closePopup}
        onToggleMinimize={toggleMinimize}
        onPopOut={popOut}
      />

      <OpportunityCalendarModal
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onOpportunityClick={(id) => openPopup(id)}
      />

      <OpportunityTaskListModal
        open={taskListOpen}
        onClose={() => setTaskListOpen(false)}
        onOpportunityClick={(id) => openPopup(id)}
      />
    </Box>
  );
}
